import React from 'react'
import { useCssHandles } from '../hooks/useCssHandles'
import { Layout } from '../shared/components'

const CSS_HANDLES = [
  'privacyPage',
  'privacyContainer',
  'privacyHeader',
  'privacyTitle',
  'privacySubtitle',
  'privacyContent',
  'privacySection',
  'privacySectionTitle',
  'privacySectionContent',
  'privacyList',
  'privacyListItem',
  'privacyHighlight',
  'privacyContact',
  'privacyTable',
  'privacyTableHeader',
  'privacyTableRow',
  'privacyTableCell'
] as const

const PrivacyPolicy: React.FC = () => {
  const handles = useCssHandles(CSS_HANDLES)

  return (
    <Layout>
      <div className={`${handles.privacyPage} min-h-screen bg-gradient-to-br from-gray-50 to-blue-50 py-16`}>
        <div className={`${handles.privacyContainer} max-w-4xl mx-auto px-6`}>
          
          {/* Header */}
          <div className={`${handles.privacyHeader} text-center mb-12`}>
            <h1 className={`${handles.privacyTitle} text-4xl md:text-5xl font-bold text-gray-800 mb-4`}>
              Política de Privacidade
            </h1>
            <p className={`${handles.privacySubtitle} text-lg text-gray-600 max-w-2xl mx-auto`}>
              Saiba como coletamos, usamos e protegemos suas informações pessoais na nossa plataforma
            </p>
          </div>

          {/* Content */}
          <div className={`${handles.privacyContent} bg-white rounded-2xl shadow-xl p-8 md:p-12 space-y-8`}>
            
            {/* Seção 1 - Introdução */}
            <section className={`${handles.privacySection}`}>
              <h2 className={`${handles.privacySectionTitle} text-2xl font-bold text-blue-600 mb-4 flex items-center`}>
                <span className="bg-blue-100 text-blue-600 rounded-full w-8 h-8 flex items-center justify-center text-sm font-bold mr-3">1</span>
                Introdução
              </h2>
              <div className={`${handles.privacySectionContent} text-gray-700 leading-relaxed space-y-4`}>
                <p>
                  A <span className={`${handles.privacyHighlight} font-semibold text-blue-600`}>Baterpower Baterias</span> 
                  está comprometida em proteger sua privacidade e garantir a segurança de suas informações pessoais.
                </p>
                <p>
                  Esta Política de Privacidade descreve como coletamos, usamos, armazenamos e protegemos suas 
                  informações quando você utiliza nosso site e serviços.
                </p>
                <p>
                  Ao utilizar nossos serviços, você concorda com a coleta e uso das informações de acordo 
                  com esta política.
                </p>
              </div>
            </section>

            {/* Seção 2 - Informações que Coletamos */}
            <section className={`${handles.privacySection}`}>
              <h2 className={`${handles.privacySectionTitle} text-2xl font-bold text-blue-600 mb-4 flex items-center`}>
                <span className="bg-blue-100 text-blue-600 rounded-full w-8 h-8 flex items-center justify-center text-sm font-bold mr-3">2</span>
                Informações que Coletamos
              </h2>
              <div className={`${handles.privacySectionContent} text-gray-700 leading-relaxed space-y-4`}>
                <p>Coletamos diferentes tipos de informações para fornecer e melhorar nossos serviços:</p>
                
                <h3 className="text-xl font-semibold text-gray-800 mt-6 mb-3">2.1 Informações Pessoais</h3>
                <ul className={`${handles.privacyList} list-disc list-inside space-y-2 ml-4`}>
                  <li className={`${handles.privacyListItem}`}>Nome completo</li>
                  <li className={`${handles.privacyListItem}`}>Endereço de e-mail</li>
                  <li className={`${handles.privacyListItem}`}>Número de telefone</li>
                  <li className={`${handles.privacyListItem}`}>Endereço de entrega</li>
                  <li className={`${handles.privacyListItem}`}>CPF/CNPJ (quando necessário)</li>
                  <li className={`${handles.privacyListItem}`}>Data de nascimento</li>
                </ul>

                <h3 className="text-xl font-semibold text-gray-800 mt-6 mb-3">2.2 Informações de Navegação</h3>
                <ul className={`${handles.privacyList} list-disc list-inside space-y-2 ml-4`}>
                  <li className={`${handles.privacyListItem}`}>Endereço IP</li>
                  <li className={`${handles.privacyListItem}`}>Tipo de navegador</li>
                  <li className={`${handles.privacyListItem}`}>Páginas visitadas</li>
                  <li className={`${handles.privacyListItem}`}>Tempo de permanência</li>
                  <li className={`${handles.privacyListItem}`}>Referências de sites</li>
                </ul>

                <h3 className="text-xl font-semibold text-gray-800 mt-6 mb-3">2.3 Informações de Pagamento</h3>
                <ul className={`${handles.privacyList} list-disc list-inside space-y-2 ml-4`}>
                  <li className={`${handles.privacyListItem}`}>Dados do cartão de crédito (criptografados)</li>
                  <li className={`${handles.privacyListItem}`}>Informações de transação</li>
                  <li className={`${handles.privacyListItem}`}>Histórico de compras</li>
                </ul>
              </div>
            </section>

            {/* Seção 3 - Como Usamos suas Informações */}
            <section className={`${handles.privacySection}`}>
              <h2 className={`${handles.privacySectionTitle} text-2xl font-bold text-blue-600 mb-4 flex items-center`}>
                <span className="bg-blue-100 text-blue-600 rounded-full w-8 h-8 flex items-center justify-center text-sm font-bold mr-3">3</span>
                Como Usamos suas Informações
              </h2>
              <div className={`${handles.privacySectionContent} text-gray-700 leading-relaxed space-y-4`}>
                <p>Utilizamos suas informações pessoais para os seguintes fins:</p>
                
                <div className={`${handles.privacyTable} bg-gray-50 rounded-xl p-6 mt-6`}>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <h4 className="font-semibold text-blue-800 mb-3 flex items-center">
                        <span className="bg-blue-200 text-blue-800 rounded-full w-6 h-6 flex items-center justify-center text-xs font-bold mr-2">✓</span>
                        Processamento de Pedidos
                      </h4>
                      <p className="text-gray-600 text-sm">Processar e entregar seus pedidos de baterias</p>
                    </div>
                    <div>
                      <h4 className="font-semibold text-blue-800 mb-3 flex items-center">
                        <span className="bg-blue-200 text-blue-800 rounded-full w-6 h-6 flex items-center justify-center text-xs font-bold mr-2">✓</span>
                        Comunicação
                      </h4>
                      <p className="text-gray-600 text-sm">Enviar confirmações e atualizações sobre pedidos</p>
                    </div>
                    <div>
                      <h4 className="font-semibold text-blue-800 mb-3 flex items-center">
                        <span className="bg-blue-200 text-blue-800 rounded-full w-6 h-6 flex items-center justify-center text-xs font-bold mr-2">✓</span>
                        Suporte ao Cliente
                      </h4>
                      <p className="text-gray-600 text-sm">Fornecer atendimento e suporte técnico</p>
                    </div>
                    <div>
                      <h4 className="font-semibold text-blue-800 mb-3 flex items-center">
                        <span className="bg-blue-200 text-blue-800 rounded-full w-6 h-6 flex items-center justify-center text-xs font-bold mr-2">✓</span>
                        Marketing
                      </h4>
                      <p className="text-gray-600 text-sm">Enviar ofertas e promoções (com seu consentimento)</p>
                    </div>
                    <div>
                      <h4 className="font-semibold text-blue-800 mb-3 flex items-center">
                        <span className="bg-blue-200 text-blue-800 rounded-full w-6 h-6 flex items-center justify-center text-xs font-bold mr-2">✓</span>
                        Melhorias
                      </h4>
                      <p className="text-gray-600 text-sm">Melhorar nossos produtos e serviços</p>
                    </div>
                    <div>
                      <h4 className="font-semibold text-blue-800 mb-3 flex items-center">
                        <span className="bg-blue-200 text-blue-800 rounded-full w-6 h-6 flex items-center justify-center text-xs font-bold mr-2">✓</span>
                        Segurança
                      </h4>
                      <p className="text-gray-600 text-sm">Prevenir fraudes e garantir a segurança</p>
                    </div>
                  </div>
                </div>
              </div>
            </section>

            {/* Seção 4 - Compartilhamento de Informações */}
            <section className={`${handles.privacySection}`}>
              <h2 className={`${handles.privacySectionTitle} text-2xl font-bold text-blue-600 mb-4 flex items-center`}>
                <span className="bg-blue-100 text-blue-600 rounded-full w-8 h-8 flex items-center justify-center text-sm font-bold mr-3">4</span>
                Compartilhamento de Informações
              </h2>
              <div className={`${handles.privacySectionContent} text-gray-700 leading-relaxed space-y-4`}>
                <p>
                  Não vendemos, alugamos ou compartilhamos suas informações pessoais com terceiros, 
                  exceto nas seguintes situações:
                </p>
                <ul className={`${handles.privacyList} list-disc list-inside space-y-2 ml-4`}>
                  <li className={`${handles.privacyListItem}`}>
                    <strong>Prestadores de serviços:</strong> Empresas que nos auxiliam na operação do site, 
                    processamento de pagamentos ou entrega de produtos
                  </li>
                  <li className={`${handles.privacyListItem}`}>
                    <strong>Cumprimento legal:</strong> Quando exigido por lei ou processo legal
                  </li>
                  <li className={`${handles.privacyListItem}`}>
                    <strong>Proteção de direitos:</strong> Para proteger nossos direitos, propriedade ou segurança
                  </li>
                  <li className={`${handles.privacyListItem}`}>
                    <strong>Consentimento:</strong> Quando você nos der permissão explícita
                  </li>
                </ul>
              </div>
            </section>

            {/* Seção 5 - Cookies e Tecnologias Similares */}
            <section className={`${handles.privacySection}`}>
              <h2 className={`${handles.privacySectionTitle} text-2xl font-bold text-blue-600 mb-4 flex items-center`}>
                <span className="bg-blue-100 text-blue-600 rounded-full w-8 h-8 flex items-center justify-center text-sm font-bold mr-3">5</span>
                Cookies e Tecnologias Similares
              </h2>
              <div className={`${handles.privacySectionContent} text-gray-700 leading-relaxed space-y-4`}>
                <p>
                  Utilizamos cookies e tecnologias similares para melhorar sua experiência em nosso site:
                </p>
                <ul className={`${handles.privacyList} list-disc list-inside space-y-2 ml-4`}>
                  <li className={`${handles.privacyListItem}`}>
                    <strong>Cookies essenciais:</strong> Necessários para o funcionamento básico do site
                  </li>
                  <li className={`${handles.privacyListItem}`}>
                    <strong>Cookies de performance:</strong> Coletam informações sobre como você usa o site
                  </li>
                  <li className={`${handles.privacyListItem}`}>
                    <strong>Cookies de funcionalidade:</strong> Lembram suas preferências e configurações
                  </li>
                  <li className={`${handles.privacyListItem}`}>
                    <strong>Cookies de marketing:</strong> Usados para exibir anúncios relevantes
                  </li>
                </ul>
                <p>
                  Você pode controlar o uso de cookies através das configurações do seu navegador.
                </p>
              </div>
            </section>

            {/* Seção 6 - Segurança dos Dados */}
            <section className={`${handles.privacySection}`}>
              <h2 className={`${handles.privacySectionTitle} text-2xl font-bold text-blue-600 mb-4 flex items-center`}>
                <span className="bg-blue-100 text-blue-600 rounded-full w-8 h-8 flex items-center justify-center text-sm font-bold mr-3">6</span>
                Segurança dos Dados
              </h2>
              <div className={`${handles.privacySectionContent} text-gray-700 leading-relaxed space-y-4`}>
                <p>
                  Implementamos medidas de segurança técnicas e organizacionais para proteger suas informações:
                </p>
                <ul className={`${handles.privacyList} list-disc list-inside space-y-2 ml-4`}>
                  <li className={`${handles.privacyListItem}`}>Criptografia SSL/TLS para transmissão de dados</li>
                  <li className={`${handles.privacyListItem}`}>Armazenamento seguro em servidores protegidos</li>
                  <li className={`${handles.privacyListItem}`}>Acesso restrito a informações pessoais</li>
                  <li className={`${handles.privacyListItem}`}>Monitoramento contínuo de segurança</li>
                  <li className={`${handles.privacyListItem}`}>Backup regular dos dados</li>
                </ul>
              </div>
            </section>

            {/* Seção 7 - Seus Direitos */}
            <section className={`${handles.privacySection}`}>
              <h2 className={`${handles.privacySectionTitle} text-2xl font-bold text-blue-600 mb-4 flex items-center`}>
                <span className="bg-blue-100 text-blue-600 rounded-full w-8 h-8 flex items-center justify-center text-sm font-bold mr-3">7</span>
                Seus Direitos
              </h2>
              <div className={`${handles.privacySectionContent} text-gray-700 leading-relaxed space-y-4`}>
                <p>De acordo com a LGPD, você tem os seguintes direitos:</p>
                <ul className={`${handles.privacyList} list-disc list-inside space-y-2 ml-4`}>
                  <li className={`${handles.privacyListItem}`}>Acessar suas informações pessoais</li>
                  <li className={`${handles.privacyListItem}`}>Corrigir dados incompletos ou incorretos</li>
                  <li className={`${handles.privacyListItem}`}>Solicitar a exclusão de seus dados</li>
                  <li className={`${handles.privacyListItem}`}>Portabilidade de dados</li>
                  <li className={`${handles.privacyListItem}`}>Revogar consentimento</li>
                  <li className={`${handles.privacyListItem}`}>Informações sobre compartilhamento</li>
                </ul>
                <p>
                  Para exercer seus direitos, entre em contato conosco através dos canais informados abaixo.
                </p>
              </div>
            </section>

            {/* Seção 8 - Retenção de Dados */}
            <section className={`${handles.privacySection}`}>
              <h2 className={`${handles.privacySectionTitle} text-2xl font-bold text-blue-600 mb-4 flex items-center`}>
                <span className="bg-blue-100 text-blue-600 rounded-full w-8 h-8 flex items-center justify-center text-sm font-bold mr-3">8</span>
                Retenção de Dados
              </h2>
              <div className={`${handles.privacySectionContent} text-gray-700 leading-relaxed space-y-4`}>
                <p>
                  Mantemos suas informações pessoais pelo tempo necessário para cumprir os propósitos 
                  descritos nesta política, incluindo:
                </p>
                <ul className={`${handles.privacyList} list-disc list-inside space-y-2 ml-4`}>
                  <li className={`${handles.privacyListItem}`}>Dados de conta: Até que você solicite a exclusão</li>
                  <li className={`${handles.privacyListItem}`}>Informações de pedidos: 5 anos (conforme legislação fiscal)</li>
                  <li className={`${handles.privacyListItem}`}>Dados de marketing: Até que você se descadastre</li>
                  <li className={`${handles.privacyListItem}`}>Logs de sistema: 12 meses</li>
                </ul>
              </div>
            </section>

            {/* Seção 9 - Alterações na Política */}
            <section className={`${handles.privacySection}`}>
              <h2 className={`${handles.privacySectionTitle} text-2xl font-bold text-blue-600 mb-4 flex items-center`}>
                <span className="bg-blue-100 text-blue-600 rounded-full w-8 h-8 flex items-center justify-center text-sm font-bold mr-3">9</span>
                Alterações na Política
              </h2>
              <div className={`${handles.privacySectionContent} text-gray-700 leading-relaxed space-y-4`}>
                <p>
                  Podemos atualizar esta Política de Privacidade periodicamente. Quando fizermos alterações 
                  significativas, notificaremos você através de:
                </p>
                <ul className={`${handles.privacyList} list-disc list-inside space-y-2 ml-4`}>
                  <li className={`${handles.privacyListItem}`}>E-mail para usuários cadastrados</li>
                  <li className={`${handles.privacyListItem}`}>Aviso destacado em nosso site</li>
                  <li className={`${handles.privacyListItem}`}>Notificação durante o login</li>
                </ul>
                <p>
                  Recomendamos que revise esta política regularmente para se manter informado sobre 
                  como protegemos suas informações.
                </p>
              </div>
            </section>

            {/* Seção 10 - Contato */}
            <section className={`${handles.privacySection}`}>
              <h2 className={`${handles.privacySectionTitle} text-2xl font-bold text-blue-600 mb-4 flex items-center`}>
                <span className="bg-blue-100 text-blue-600 rounded-full w-8 h-8 flex items-center justify-center text-sm font-bold mr-3">10</span>
                Contato
              </h2>
              <div className={`${handles.privacySectionContent} text-gray-700 leading-relaxed space-y-4`}>
                <p>
                  Se você tiver dúvidas sobre esta Política de Privacidade ou sobre como tratamos suas 
                  informações pessoais, entre em contato conosco:
                </p>
                <div className={`${handles.privacyContact} bg-blue-50 rounded-xl p-6 mt-6`}>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <h4 className="font-semibold text-blue-800 mb-2">📧 E-mail</h4>
                      <p className="text-blue-700">privacidade@baterpowerbaterias.com.br</p>
                    </div>
                    <div>
                      <h4 className="font-semibold text-blue-800 mb-2">📞 Telefone</h4>
                      <p className="text-blue-700">(19) 3255-2356</p>
                    </div>
                    <div>
                      <h4 className="font-semibold text-blue-800 mb-2">📍 Endereço</h4>
                      <p className="text-blue-700">
                        Av. José de Sousa Campos, 2086<br />
                        Nova Campinas, Campinas - SP<br />
                        CEP: 13025-320
                      </p>
                    </div>
                    <div>
                      <h4 className="font-semibold text-blue-800 mb-2">🕒 Horário</h4>
                      <p className="text-blue-700">
                        Seg-Sex: 8h às 18h<br />
                        Sáb: 8h às 14h
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </section>

            {/* Data de Atualização */}
            <div className="border-t border-gray-200 pt-8 mt-12">
              <p className="text-sm text-gray-500 text-center">
                <strong>Última atualização:</strong> {new Date().toLocaleDateString('pt-BR')}
              </p>
            </div>
          </div>
        </div>
      </div>
    </Layout>
  )
}

export default PrivacyPolicy
